"use client";

import { trpc } from "@/lib/trpc";
import SiteNav from "@/components/SiteNav";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { ArrowRight, BookOpen, MapPin, Car, Scale, Zap, Newspaper } from "lucide-react";
import { useState } from "react";

const CATEGORY_ICONS: Record<string, { icon: typeof BookOpen; color: string }> = {
  "guide-pays": { icon: MapPin, color: "bg-blue-100 text-blue-700" },
  "fiche-modele": { icon: Car, color: "bg-green-100 text-green-700" },
  "comparatif": { icon: Scale, color: "bg-purple-100 text-purple-700" },
  "glossaire": { icon: BookOpen, color: "bg-amber-100 text-amber-700" },
  "type-vehicule": { icon: Zap, color: "bg-cyan-100 text-cyan-700" },
  "actualites": { icon: Newspaper, color: "bg-red-100 text-red-700" },
};

const CATEGORY_KEYS: Record<string, string> = {
  "guide-pays": "guidePays",
  "fiche-modele": "ficheModele",
  "comparatif": "comparatif",
  "glossaire": "glossaire",
  "type-vehicule": "typeVehicule",
  "actualites": "actualites",
};

export default function BlogIndex() {
  const t = useTranslations("blog");
  const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined);
  const { data: posts = [], isLoading } = trpc.blog.list.useQuery(
    activeCategory ? { category: activeCategory } : undefined
  );

  return (
    <>
      <SiteNav />
      <main className="pt-24 pb-16 min-h-screen bg-offwhite">
        <div className="container max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-graphite font-heading">
              {t("title")}
            </h1>
            <p className="mt-4 text-lg text-graphite/60 max-w-2xl">
              {t("subtitle")}
            </p>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button
              onClick={() => setActiveCategory(undefined)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                !activeCategory
                  ? "bg-graphite text-white"
                  : "bg-warm-gray text-graphite/70 hover:text-graphite"
              }`}
            >
              {t("all")}
            </button>
            {Object.entries(CATEGORY_ICONS).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(activeCategory === key ? undefined : key)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeCategory === key
                    ? "bg-graphite text-white"
                    : "bg-warm-gray text-graphite/70 hover:text-graphite"
                }`}
              >
                {t(`categories.${CATEGORY_KEYS[key]}`)}
              </button>
            ))}
          </div>

          {/* Posts grid */}
          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border border-border animate-pulse">
                  <div className="h-44 bg-warm-gray" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 w-20 bg-warm-gray rounded" />
                    <div className="h-5 w-full bg-warm-gray rounded" />
                    <div className="h-4 w-3/4 bg-warm-gray rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 text-graphite/40">
              <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">{t("noArticles")}</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => {
                const catMeta = CATEGORY_ICONS[post.category] || CATEGORY_ICONS["glossaire"];
                const Icon = catMeta.icon;
                const catKey = CATEGORY_KEYS[post.category] || "glossaire";
                return (
                  <Link
                    key={post.id}
                    href={`/blog/${post.slug}`}
                    className="group bg-white border border-border hover:border-swiss-red/30 transition-all hover:shadow-lg"
                  >
                    {post.featuredImage ? (
                      <div className="h-44 overflow-hidden">
                        <img
                          src={post.featuredImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="h-44 bg-gradient-to-br from-warm-gray to-offwhite flex items-center justify-center">
                        <Icon size={40} className="text-graphite/20" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${catMeta.color}`}>
                          <Icon size={12} /> {t(`categories.${catKey}`)}
                        </span>
                        {post.country && (
                          <span className="text-xs text-graphite/40">
                            {t(`countries.${post.country}`)}
                          </span>
                        )}
                      </div>
                      <h2 className="text-lg font-semibold text-graphite group-hover:text-swiss-red transition-colors font-heading leading-tight">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="mt-2 text-sm text-graphite/60 line-clamp-2">{post.excerpt}</p>
                      )}
                      <div className="mt-4 flex items-center gap-1 text-sm font-medium text-swiss-red opacity-0 group-hover:opacity-100 transition-opacity">
                        {t("readArticle")} <ArrowRight size={14} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
